(() => {
  const root = document.querySelector('[data-qva-account-gateway]');
  if (!root || root.dataset.mode === 'off') return;

  const dialog = root.querySelector('[role="dialog"]');
  if (!(dialog instanceof HTMLElement)) return;

  const loginButton = root.querySelector('[data-qva-gateway-login]');
  const guestButton = root.querySelector('[data-qva-gateway-guest]');
  const closeButtons = root.querySelectorAll('[data-qva-gateway-close]');
  const errorBox = root.querySelector('[data-qva-gateway-error]');

  const DRAFT_THEME_ID = '188538847527';
  const LOGIN_TIMEOUT_MS = 10000;
  const CART_IDLE_TIMEOUT_MS = 10000;
  const CHECKOUT_BUTTON_TIMEOUT_MS = 2000;
  const BUY_NOW_TIMEOUT_MS = 15000;
  const BUY_NOW_WALLET_RETRY_TIMEOUT_MS = 30000;
  const RESUME_MAX_AGE_MS = 15 * 60 * 1000;
  const WALLET_BUSY_MESSAGE =
    'Ví điểm vẫn đang xử lý. Vui lòng chờ cập nhật hoàn tất rồi thử thanh toán lại.';
  const legacyGuestTestKey = 'qvaGatewayTestGuest';
  const resumeCheckoutKey = 'qvaSignupResumeCheckout';

  /* A checkout continuation is authority to submit one exact cart form once.
     Cart Select can claim that authority synchronously, hold it while its
     verified cleanup runs, and return it only for the final native submit.
     This is event-bounded rather than time-bounded: a slow request cannot lose
     authority, and a stalled/failed request leaves no bypass on the form. */
  const nativeCheckoutContinuations = new WeakMap();
  const NATIVE_CONTINUATION_ATTR = 'data-qva-gateway-continuation';
  const NATIVE_CONTINUATION_CLAIM = 'qva:account-gateway:continuation-claim';
  const NATIVE_CONTINUATION_READY = 'qva:account-gateway:continuation-ready';
  const NATIVE_CONTINUATION_CANCEL = 'qva:account-gateway:continuation-cancel';
  let nativeContinuationSequence = 0;
  let pendingCheckout = null;

  function paintCheckoutProgress(record) {
    if (pendingCheckout !== record) return;
    const label = record.state === 'consumed' ? 'Đang mở trang thanh toán…' : 'Đang chuẩn bị thanh toán…';
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (button.tagName !== 'BUTTON') return;
      if (!record.progressButtons.has(button)) {
        record.progressButtons.set(button, {
          html: button.innerHTML,
          busy: button.getAttribute('aria-busy'),
          label: button.getAttribute('aria-label'),
        });
      }
      if (button.textContent !== label) button.textContent = label;
      if (!button.querySelector('[data-qva-checkout-spinner]')) {
        const spinner = document.createElement('span');
        spinner.setAttribute('data-qva-checkout-spinner', '');
        spinner.setAttribute('aria-hidden', 'true');
        button.appendChild(spinner);
      }
      button.setAttribute('data-qva-checkout-pending', 'true');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-label', label);
    });
  }

  function beginCheckoutProgress(record) {
    pendingCheckout = record;
    record.progressButtons = new Map();
    paintCheckoutProgress(record);
    record.progressObserver = new MutationObserver(() => paintCheckoutProgress(record));
    record.progressObserver.observe(document.body, { childList: true, subtree: true });
  }

  function clearCheckoutProgress(record) {
    if (!record || pendingCheckout !== record) return;
    pendingCheckout = null;
    record.progressObserver?.disconnect();
    record.progressButtons?.forEach((saved, button) => {
      button.innerHTML = saved.html;
      button.removeAttribute('data-qva-checkout-pending');
      for (const [name, value] of [['aria-busy', saved.busy], ['aria-label', saved.label]]) {
        if (value === null) button.removeAttribute(name);
        else button.setAttribute(name, value);
      }
    });
  }

  function blockDuplicateCheckout(event, button) {
    if (!pendingCheckout || nativeContinuationIsActive(button)) return false;
    event.preventDefault();
    event.stopImmediatePropagation?.();
    return true;
  }

  window.addEventListener('pageshow', (event) => {
    // Back from checkout must restore usable controls, without releasing an
    // unfinished cart transaction merely because the page becomes visible.
    if (event.persisted && pendingCheckout?.state === 'consumed') clearCheckoutProgress(pendingCheckout);
  });
  const cartMutationTokens = new Set();
  const protectedCartMutationTokens = new Set();
  let anonymousCartMutationDepth = 0;
  let anonymousProtectedMutationDepth = 0;
  let activeIntent = null;
  let intentSequence = 0;
  let loginOperation = null;
  let pendingBuyNow = null;
  let buyNowSequence = 0;
  let cartFallbackStarted = false;

  function safeStorageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function safeStorageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function safeStorageJson(key) {
    const raw = safeStorageGet(key);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      safeStorageRemove(key);
      return null;
    }
  }

  const guestTestParam = new URLSearchParams(window.location.search).get('qva_gateway_test');
  const guestTestAllowed =
    root.dataset.qvaTestAllowed === 'true' && String(root.dataset.qvaThemeId || '') === DRAFT_THEME_ID;
  const draftForcesGuest = guestTestAllowed && guestTestParam === 'guest';

  // The old draft test persisted for two hours and could override a real login
  // after the tester navigated away. Guest simulation is now current-page only.
  // Remove the legacy flag on every load so already-open testing tabs self-heal.
  safeStorageRemove(legacyGuestTestKey);

  const customerAuthenticated = root.dataset.customerAuthenticated === 'true' && !draftForcesGuest;
  const analyticsContext = {
    experiment: root.dataset.experiment || 'qva_account_gateway',
    variant: root.dataset.variant || root.dataset.mode || 'optional',
  };
  const analyticsFieldAllowlist = ['experiment', 'variant', 'surface', 'choice', 'mode'];

  function publishAnalytics(name, detail) {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      const analytics = window.Shopify?.analytics;
      if (
        typeof privacy?.analyticsProcessingAllowed !== 'function' ||
        privacy.analyticsProcessingAllowed() !== true ||
        typeof analytics?.publish !== 'function'
      ) {
        return;
      }

      const source = { ...analyticsContext, ...detail };
      const payload = {};
      analyticsFieldAllowlist.forEach((field) => {
        const value = source[field];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          payload[field] = value;
        }
      });

      const publication = analytics.publish(`qva_auth:${name}`, payload);
      if (publication && typeof publication.catch === 'function') {
        publication.catch(() => {});
      }
    } catch (error) {
      // Consent or analytics failures must never affect checkout.
    }
  }

  function emit(name, detail = {}) {
    try {
      window.dispatchEvent(new CustomEvent(`qva:account-gateway:${name}`, { detail }));
    } catch (error) {
      // The gateway remains functional if another script replaces CustomEvent.
    }

    publishAnalytics(name, detail);
  }

  function isCheckoutButton(element) {
    return element instanceof HTMLElement && element.matches('[name="checkout"]');
  }

  function isBuyNowButton(element) {
    return element instanceof HTMLElement && element.matches('[data-qva-buy-now]');
  }

  function checkoutSurface(button) {
    if (button?.closest('cart-drawer')) return 'drawer';
    if (button?.closest('cart-notification')) return 'notification';
    return 'cart-page';
  }

  function selectorForSurface(surface) {
    if (surface === 'drawer') return 'cart-drawer [name="checkout"]';
    if (surface === 'notification') return 'cart-notification [name="checkout"]';
    return 'main [name="checkout"]';
  }

  function checkoutButtonIsUsable(button) {
    return Boolean(
      isCheckoutButton(button) &&
        button.isConnected &&
        button.form &&
        !button.disabled &&
        button.getAttribute('aria-disabled') !== 'true'
    );
  }

  function firstUsableCheckoutButton(selector) {
    const candidates = Array.from(document.querySelectorAll(selector)).filter(checkoutButtonIsUsable);
    return candidates.find((candidate) => candidate.offsetParent !== null) || candidates[0] || null;
  }

  function resolveCheckoutButton(intentOrSurface) {
    const surface = typeof intentOrSurface === 'string' ? intentOrSurface : intentOrSurface?.surface;
    const surfaceButton = firstUsableCheckoutButton(selectorForSurface(surface));
    if (surfaceButton) return surfaceButton;

    return (
      firstUsableCheckoutButton('cart-drawer [name="checkout"]') ||
      firstUsableCheckoutButton('cart-notification [name="checkout"]') ||
      firstUsableCheckoutButton('main [name="checkout"]') ||
      firstUsableCheckoutButton('[name="checkout"]')
    );
  }

  function checkoutButtonFromSubmitEvent(event) {
    if (isCheckoutButton(event.submitter)) return event.submitter;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return null;

    // CartDrawer-Checkout is intentionally outside its <form> and linked with
    // form="CartDrawer-Form". Some in-app browsers omit SubmitEvent.submitter
    // for that shape, so searching only descendants silently misses checkout
    // and posts back to /cart without the checkout field.
    const candidates = Array.from(document.querySelectorAll('[name="checkout"]')).filter(
      (candidate) => candidate.form === form && checkoutButtonIsUsable(candidate)
    );
    return candidates.find((candidate) => candidate.offsetParent !== null) || candidates[0] || null;
  }

  function showError(message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = !message;
  }

  function setBusy(busy) {
    if (loginButton) {
      loginButton.disabled = busy;
      loginButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    if (guestButton) guestButton.disabled = busy;
  }

  function focusableElements() {
    return Array.from(
      dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.offsetParent !== null && element.tabIndex >= 0);
  }

  function gatewayIsOpen() {
    return root.getAttribute('aria-hidden') === 'false';
  }

  function createAbortHandle() {
    if (typeof AbortController === 'function') return new AbortController();
    return { signal: null, abort() {} };
  }

  function createCheckoutIntent(button) {
    return Object.freeze({
      id: ++intentSequence,
      surface: checkoutSurface(button),
      focusOrigin: button,
      createdAt: Date.now(),
      controller: createAbortHandle(),
    });
  }

  function intentIsCurrent(intent) {
    return Boolean(intent && activeIntent === intent && !intent.controller.signal?.aborted);
  }

  function cancelIntent(intent) {
    if (!intent || intent.controller.signal?.aborted) return;
    try {
      intent.controller.abort();
    } catch (error) {
      // Older AbortController implementations can throw after navigation starts.
    }
  }

  function abortLogin(reason = 'cancel') {
    const operation = loginOperation;
    if (!operation) return;

    operation.abortReason = reason;
    window.clearTimeout(operation.timeoutId);
    try {
      operation.controller.abort();
    } catch (error) {
      // The operation is already ending.
    }
  }

  function replaceActiveIntent(button) {
    const previous = activeIntent;
    activeIntent = null;
    abortLogin('superseded');
    cancelIntent(previous);

    const intent = createCheckoutIntent(button);
    activeIntent = intent;
    return intent;
  }

  function restoreIntentFocus(intent) {
    let target = intent?.focusOrigin;
    if (!(target instanceof HTMLElement) || !target.isConnected || target.offsetParent === null) {
      target = resolveCheckoutButton(intent);
    }
    if (!(target instanceof HTMLElement)) return;

    window.setTimeout(() => {
      if (!gatewayIsOpen() && target.isConnected) {
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          target.focus();
        }
      }
    }, 0);
  }

  function openGateway(intent, { allowMissingButton = false } = {}) {
    if (!intentIsCurrent(intent) || gatewayIsOpen()) return;

    const button = resolveCheckoutButton(intent);
    if (!button && !allowMissingButton) return;

    showError('');
    setBusy(false);
    root.setAttribute('aria-hidden', 'false');
    document.body?.classList.add('qva-account-gateway-open');
    dialog.focus();
    window.requestAnimationFrame(() => {
      if (gatewayIsOpen() && intentIsCurrent(intent)) dialog.focus();
    });
    emit('open', { surface: intent.surface, mode: root.dataset.mode });
  }

  function showProtectedWalletNotice() {
    let globalNotice = document.getElementById('QvaWalletCheckoutNotice');
    if (!(globalNotice instanceof HTMLElement)) {
      globalNotice = document.createElement('div');
      globalNotice.id = 'QvaWalletCheckoutNotice';
      globalNotice.className = 'qva-wallet-checkout-notice';
      globalNotice.setAttribute('role', 'status');
      globalNotice.setAttribute('aria-live', 'assertive');
      document.body?.appendChild(globalNotice);
    }
    globalNotice.textContent = WALLET_BUSY_MESSAGE;
    globalNotice.removeAttribute('hidden');

    const wallet = document.querySelector('[data-qva-wallet]');
    if (!(wallet instanceof HTMLElement)) return;

    let notice = wallet.querySelector('[data-qva-wallet-checkout-notice]');
    if (!(notice instanceof HTMLElement)) {
      notice = document.createElement('p');
      notice.className = 'qva-wal__msg qva-wal__msg--err';
      notice.dataset.qvaWalletCheckoutNotice = '';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      wallet.appendChild(notice);
    }
    notice.textContent = WALLET_BUSY_MESSAGE;
    notice.removeAttribute('hidden');
    try {
      wallet.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (error) {
      wallet.scrollIntoView();
    }
  }

  function showProtectedWalletMessage(intent, { openDialog = true } = {}) {
    emit('wallet-protected-timeout', { surface: intent?.surface, mode: root.dataset.mode });
    showProtectedWalletNotice();
    if (openDialog) {
      if (intentIsCurrent(intent) && !gatewayIsOpen()) openGateway(intent, { allowMissingButton: true });
      showError(WALLET_BUSY_MESSAGE);
    }
    setBusy(false);
  }

  function hideGateway(reason = 'close', intent = activeIntent, restoreFocus = true) {
    const wasOpen = gatewayIsOpen();
    root.setAttribute('aria-hidden', 'true');
    document.body?.classList.remove('qva-account-gateway-open');
    if (wasOpen) emit('close', { reason });
    if (restoreFocus) restoreIntentFocus(intent);
  }

  function closeGateway(reason = 'close') {
    const intent = activeIntent;
    activeIntent = null;
    abortLogin(reason);
    cancelIntent(intent);
    setBusy(false);
    hideGateway(reason, intent, true);
  }

  function mutationToken(event) {
    const token = event?.detail?.token;
    if (typeof token !== 'string' && typeof token !== 'number') return '';
    return String(token);
  }

  function mutationAttributeIsBusy(element) {
    if (!(element instanceof Element) || !element.hasAttribute('data-qva-cart-mutation-busy')) return false;
    const value = String(element.getAttribute('data-qva-cart-mutation-busy') || '').toLowerCase();
    return value !== 'false' && value !== '0' && value !== 'off';
  }

  function walletMutationAttributeIsBusy(element) {
    if (!(element instanceof Element) || !element.hasAttribute('data-qva-wallet-mutation-busy')) return false;
    const value = String(element.getAttribute('data-qva-wallet-mutation-busy') || '').toLowerCase();
    return value !== 'false' && value !== '0' && value !== 'off';
  }

  function mutationIsProtected(event) {
    return event?.detail?.protected === true || event?.detail?.source === 'wallet';
  }

  function walletMutationIsBusy() {
    return Boolean(
      protectedCartMutationTokens.size ||
        anonymousProtectedMutationDepth ||
        walletMutationAttributeIsBusy(document.documentElement) ||
        walletMutationAttributeIsBusy(document.body)
    );
  }

  function cartMutationIsBusy() {
    return Boolean(
      cartMutationTokens.size ||
        anonymousCartMutationDepth ||
        walletMutationIsBusy() ||
        mutationAttributeIsBusy(document.documentElement) ||
        mutationAttributeIsBusy(document.body)
    );
  }

  document.addEventListener('qva:cart-mutation-start', (event) => {
    const token = mutationToken(event);
    const protectedMutation = mutationIsProtected(event);
    if (token) {
      cartMutationTokens.add(token);
      if (protectedMutation) protectedCartMutationTokens.add(token);
    } else {
      anonymousCartMutationDepth += 1;
      if (protectedMutation) anonymousProtectedMutationDepth += 1;
    }
  });

  document.addEventListener('qva:cart-mutation-end', (event) => {
    const token = mutationToken(event);
    const protectedMutation = mutationIsProtected(event);
    if (token) {
      cartMutationTokens.delete(token);
      protectedCartMutationTokens.delete(token);
    } else {
      anonymousCartMutationDepth = Math.max(0, anonymousCartMutationDepth - 1);
      if (protectedMutation) {
        anonymousProtectedMutationDepth = Math.max(0, anonymousProtectedMutationDepth - 1);
      }
    }
    if (!walletMutationIsBusy()) document.getElementById('QvaWalletCheckoutNotice')?.remove();
    if (protectedMutation) retryTimedOutBuyNowFallbackAfterWallet();
  });

  document.addEventListener('qva:wallet-navigation-blocked', () => {
    if (walletMutationIsBusy()) showProtectedWalletNotice();
  });

  function waitForCartIdle({ timeout = CART_IDLE_TIMEOUT_MS, signal = null, isCurrent = () => true } = {}) {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      let timerId = null;
      let settled = false;

      const finish = (status) => {
        if (settled) return;
        settled = true;
        if (timerId) window.clearTimeout(timerId);
        if (signal && typeof signal.removeEventListener === 'function') {
          signal.removeEventListener('abort', onAbort);
        }
        resolve(status);
      };

      const onAbort = () => finish('aborted');
      const check = () => {
        if (signal?.aborted || !isCurrent()) {
          finish('aborted');
        } else if (!cartMutationIsBusy()) {
          finish('idle');
        } else if (Date.now() - startedAt >= timeout) {
          finish(walletMutationIsBusy() ? 'protected-timeout' : 'timeout');
        } else {
          timerId = window.setTimeout(check, 50);
        }
      };

      if (signal && typeof signal.addEventListener === 'function') {
        signal.addEventListener('abort', onAbort, { once: true });
      }
      check();
    });
  }

  function waitForCheckoutButton(intentOrSurface, {
    timeout = CHECKOUT_BUTTON_TIMEOUT_MS,
    signal = null,
    isCurrent = () => true,
  } = {}) {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      let timerId = null;
      let settled = false;

      const finish = (button) => {
        if (settled) return;
        settled = true;
        if (timerId) window.clearTimeout(timerId);
        if (signal && typeof signal.removeEventListener === 'function') {
          signal.removeEventListener('abort', onAbort);
        }
        resolve(button);
      };

      const onAbort = () => finish(null);
      const check = () => {
        if (signal?.aborted || !isCurrent()) {
          finish(null);
          return;
        }

        const button = resolveCheckoutButton(intentOrSurface);
        if (button) {
          finish(button);
        } else if (Date.now() - startedAt >= timeout) {
          finish(null);
        } else {
          timerId = window.setTimeout(check, 50);
        }
      };

      if (signal && typeof signal.addEventListener === 'function') {
        signal.addEventListener('abort', onAbort, { once: true });
      }
      check();
    });
  }

  function clearGuestTestMode() {
    safeStorageRemove(legacyGuestTestKey);
  }

  function sanitizedLoginUrl(rawLoginUrl) {
    const loginUrl = new URL(rawLoginUrl, window.location.origin);
    const returnTo = loginUrl.searchParams.get('return_to');

    loginUrl.searchParams.delete('qva_gateway_test');
    if (returnTo) {
      const returnUrl = new URL(returnTo, window.location.origin);
      returnUrl.searchParams.delete('qva_gateway_test');
      loginUrl.searchParams.set('return_to', `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);
    }

    return `${loginUrl.pathname}${loginUrl.search}${loginUrl.hash}`;
  }

  function cartFallbackUrl() {
    if (window.routes?.cart_url) return window.routes.cart_url;
    if (window.Shopify?.routes?.cart_url) return window.Shopify.routes.cart_url;
    const shopRoot = window.Shopify?.routes?.root || '/';
    return (shopRoot.endsWith('/') ? shopRoot : shopRoot + '/') + 'cart';
  }

  function navigateToCartFallback(reason, surface) {
    if (walletMutationIsBusy()) {
      emit('wallet-protected-timeout', { surface, mode: root.dataset.mode });
      showProtectedWalletNotice();
      return false;
    }
    if (cartFallbackStarted) return true;
    cartFallbackStarted = true;
    emit('fallback', { reason, surface, mode: root.dataset.mode });

    const target = cartFallbackUrl();
    try {
      window.location.assign(target);
    } catch (error) {
      try {
        window.location.href = target;
      } catch (navigationError) {
        cartFallbackStarted = false;
        return false;
      }
    }
    return true;
  }

  function formIsValid(button) {
    const form = button?.form;
    if (!form || typeof form.reportValidity !== 'function') return true;
    return form.reportValidity();
  }

  function cartPayload(form) {
    const attributes = {};
    const controls = Array.from(form.elements || []);

    controls.filter((field) => field.name?.startsWith('attributes[')).forEach((field) => {
      if (field.disabled) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      const match = field.name.match(/^attributes\[(.*)]$/);
      if (match) attributes[match[1]] = field.value;
    });

    const noteField = controls.find((field) => field.name === 'note');
    const payload = {};
    if (Object.keys(attributes).length) payload.attributes = attributes;
    if (noteField && !noteField.disabled) payload.note = noteField.value;
    return payload;
  }

  async function persistCartFields(button, signal) {
    const form = button?.form;
    if (!form) return;
    const payload = cartPayload(form);
    if (!Object.keys(payload).length) return;

    const options = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    };
    if (signal) options.signal = signal;

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, options);
    if (!response.ok) throw new Error(`cart-update-${response.status}`);
  }

  function nativeContinuationToken() {
    nativeContinuationSequence += 1;
    return `qva-checkout-${Date.now().toString(36)}-${nativeContinuationSequence.toString(36)}`;
  }

  function nativeContinuationFor(button) {
    const form = button?.form;
    if (!(form instanceof HTMLFormElement)) return null;
    const token = form.getAttribute(NATIVE_CONTINUATION_ATTR) || '';
    const record = nativeCheckoutContinuations.get(form);
    if (
      !token ||
      !record ||
      record.token !== token ||
      record.form !== form ||
      record.button !== button ||
      (record.state !== 'issued' && record.state !== 'ready')
    ) {
      if (token) form.removeAttribute(NATIVE_CONTINUATION_ATTR);
      return null;
    }
    return record;
  }

  function revokeNativeContinuation(record, state = 'cancelled') {
    if (!record) return;
    if (nativeCheckoutContinuations.get(record.form) === record) {
      nativeCheckoutContinuations.delete(record.form);
    }
    if (record.form.getAttribute(NATIVE_CONTINUATION_ATTR) === record.token) {
      record.form.removeAttribute(NATIVE_CONTINUATION_ATTR);
    }
    record.checkoutMarker?.remove();
    record.checkoutMarker = null;
    record.state = state;
    clearCheckoutProgress(record);
  }

  function consumeNativeContinuation(record) {
    if (!record) return;
    if (nativeCheckoutContinuations.get(record.form) === record) {
      nativeCheckoutContinuations.delete(record.form);
    }
    if (record.form.getAttribute(NATIVE_CONTINUATION_ATTR) === record.token) {
      record.form.removeAttribute(NATIVE_CONTINUATION_ATTR);
    }
    const checkoutMarker = record.checkoutMarker;
    record.checkoutMarker = null;
    record.state = 'consumed';
    paintCheckoutProgress(record);
    /* The browser constructs the form entry list after submit listeners run.
       Keep the drawer fallback marker through that serialization step, while
       consuming its checkout authority immediately so it cannot be replayed. */
    if (checkoutMarker) setTimeout(() => checkoutMarker.remove(), 0);
  }

  function issueNativeContinuation(button, surface) {
    const form = button?.form;
    if (!(form instanceof HTMLFormElement)) return null;
    revokeNativeContinuation(nativeCheckoutContinuations.get(form), 'superseded');
    const record = {
      token: nativeContinuationToken(),
      form,
      button,
      surface,
      state: 'issued',
    };
    nativeCheckoutContinuations.set(form, record);
    form.setAttribute(NATIVE_CONTINUATION_ATTR, record.token);
    beginCheckoutProgress(record);
    return record;
  }

  function nativeContinuationIsActive(button) {
    return Boolean(nativeContinuationFor(button));
  }

  function continuationDetail(event) {
    const detail = event?.detail;
    if (!detail || detail.source !== 'qva-cart-select') return null;
    const form = detail.form;
    if (!(form instanceof HTMLFormElement)) return null;
    const token = typeof detail.token === 'string' ? detail.token : '';
    const record = nativeCheckoutContinuations.get(form);
    if (!token || !record || record.token !== token || record.form !== form) return null;
    return { detail, record };
  }

  document.addEventListener(NATIVE_CONTINUATION_CLAIM, (event) => {
    const match = continuationDetail(event);
    if (!match || match.record.state !== 'issued') return;
    if (match.record.form.getAttribute(NATIVE_CONTINUATION_ATTR) !== match.record.token) return;
    match.record.form.removeAttribute(NATIVE_CONTINUATION_ATTR);
    match.record.state = 'claimed';
    match.detail.accepted = true;
  });

  document.addEventListener(NATIVE_CONTINUATION_READY, (event) => {
    const match = continuationDetail(event);
    if (!match || match.record.state !== 'claimed') return;
    if (match.record.form.hasAttribute(NATIVE_CONTINUATION_ATTR)) return;
    match.record.state = 'ready';
    match.record.form.setAttribute(NATIVE_CONTINUATION_ATTR, match.record.token);
    match.detail.accepted = true;
  });

  document.addEventListener(NATIVE_CONTINUATION_CANCEL, (event) => {
    const match = continuationDetail(event);
    if (!match || match.record.state === 'consumed') return;
    revokeNativeContinuation(match.record);
    match.detail.accepted = true;
  });

  function submitNativeCheckout(button, surface = checkoutSurface(button)) {
    if (pendingCheckout) return false;
    const liveButton = resolveCheckoutButton(surface) || button;
    if (!checkoutButtonIsUsable(liveButton) || !formIsValid(liveButton)) return false;

    const form = liveButton.form;
    let checkoutMarker = null;
    if (surface === 'drawer') {
      // Keep the checkout intent in the serialized form even in browsers that
      // lose the external submitter. The marker exists only for this explicit
      // checkout continuation and is removed if navigation is prevented.
      checkoutMarker = document.createElement('input');
      checkoutMarker.type = 'hidden';
      checkoutMarker.name = 'checkout';
      checkoutMarker.value = '1';
      checkoutMarker.setAttribute('data-qva-native-checkout-marker', 'true');
      form.appendChild(checkoutMarker);
    }
    const continuation = issueNativeContinuation(liveButton, surface);
    if (!continuation) {
      checkoutMarker?.remove();
      return false;
    }
    continuation.checkoutMarker = checkoutMarker;
    const cleanupUnclaimed = () => {
      /* requestSubmit/click dispatches submit synchronously. If neither this
         gateway nor Cart Select claimed the continuation before it returned,
         no trusted path owns it and it must be revoked immediately. */
      if (continuation.state === 'issued') revokeNativeContinuation(continuation);
    };

    if (typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit(liveButton);
        cleanupUnclaimed();
        return true;
      } catch (error) {
        /* Once Cart Select has claimed the token it exclusively owns this
           attempt. A legacy click fallback here could create a second path. */
        if (continuation.state !== 'issued') {
          revokeNativeContinuation(continuation);
          return false;
        }
        // Fall through for legacy/partially implemented requestSubmit.
      }
    }

    try {
      // The continuation is consumed by submit, not click, so the legacy path
      // cannot reopen the modal between those two synchronous events.
      liveButton.click();
      cleanupUnclaimed();
      return true;
    } catch (error) {
      revokeNativeContinuation(continuation);
      return false;
    }
  }

  async function continueOriginalCheckout(intent, source, { waitForMutation = true } = {}) {
    if (!intentIsCurrent(intent)) return;

    if (waitForMutation && cartMutationIsBusy()) {
      setBusy(true);
      const mutationStatus = await waitForCartIdle({
        signal: intent.controller.signal,
        isCurrent: () => intentIsCurrent(intent),
      });
      if (mutationStatus === 'aborted' || !intentIsCurrent(intent)) return;
      if (mutationStatus === 'protected-timeout') {
        showProtectedWalletMessage(intent);
        return;
      }
      if (mutationStatus === 'timeout') emit('mutation-timeout', { surface: intent.surface });
      setBusy(false);
    }
    if (walletMutationIsBusy()) {
      showProtectedWalletMessage(intent);
      return;
    }

    const button = await waitForCheckoutButton(intent, {
      signal: intent.controller.signal,
      isCurrent: () => intentIsCurrent(intent),
    });
    if (!intentIsCurrent(intent)) return;
    if (walletMutationIsBusy()) {
      showProtectedWalletMessage(intent);
      return;
    }

    if (!button) {
      activeIntent = null;
      cancelIntent(intent);
      hideGateway('checkout-missing', intent, false);
      navigateToCartFallback('checkout-button-timeout', intent.surface);
      return;
    }

    if (!formIsValid(button)) {
      showError('Không tìm thấy nút thanh toán hợp lệ. Vui lòng đóng cửa sổ và thử lại.');
      setBusy(false);
      return;
    }
    if (handleWholesaleFirstClick({ preventDefault() {} }, button)) {
      activeIntent = null;
      cancelIntent(intent);
      setBusy(false);
      hideGateway('wholesale-first', intent, false);
      return;
    }

    safeStorageRemove(resumeCheckoutKey);
    if (source === 'guest') {
      emit('choice', { choice: 'guest', surface: intent.surface, mode: root.dataset.mode });
    }

    abortLogin(source);
    activeIntent = null;
    cancelIntent(intent);
    setBusy(false);
    hideGateway(source, intent, false);
    emit('continue', {
      source,
      choice: source === 'guest' ? 'guest' : source === 'signed-in-resume' ? 'login' : 'direct',
      surface: intent.surface,
      mode: root.dataset.mode,
    });

    submitNativeCheckout(button, intent.surface);
  }

  async function prepareCheckoutIntent(intent, { skipMutationWait = false } = {}) {
    const mutationStatus = skipMutationWait
      ? 'timeout'
      : await waitForCartIdle({
          signal: intent.controller.signal,
          isCurrent: () => intentIsCurrent(intent),
        });

    if (mutationStatus === 'aborted' || !intentIsCurrent(intent)) return;

    const button = await waitForCheckoutButton(intent, {
      signal: intent.controller.signal,
      isCurrent: () => intentIsCurrent(intent),
    });
    if (!intentIsCurrent(intent)) return;

    if (!button) {
      if (mutationStatus === 'protected-timeout' || walletMutationIsBusy()) {
        showProtectedWalletMessage(intent);
        return;
      }
      activeIntent = null;
      cancelIntent(intent);
      navigateToCartFallback('checkout-button-timeout', intent.surface);
      return;
    }

    if (!formIsValid(button)) {
      activeIntent = null;
      cancelIntent(intent);
      return;
    }
    if (mutationStatus === 'protected-timeout' || walletMutationIsBusy()) {
      showProtectedWalletMessage(intent);
      return;
    }
    if (handleWholesaleFirstClick({ preventDefault() {} }, button)) {
      activeIntent = null;
      cancelIntent(intent);
      return;
    }

    if (mutationStatus === 'timeout') {
      emit('mutation-timeout', { surface: intent.surface });
      await continueOriginalCheckout(intent, 'mutation-timeout', { waitForMutation: false });
      return;
    }

    openGateway(intent);
  }

  function beginCheckoutIntent(button, options = {}) {
    if (gatewayIsOpen() && activeIntent) return;
    const intent = replaceActiveIntent(button);
    void prepareCheckoutIntent(intent, options);
  }

  async function prepareSignedInCheckout(intent) {
    const mutationStatus = await waitForCartIdle({
      signal: intent.controller.signal,
      isCurrent: () => intentIsCurrent(intent),
    });
    if (mutationStatus === 'aborted' || !intentIsCurrent(intent)) return;
    if (mutationStatus === 'protected-timeout' || walletMutationIsBusy()) {
      showProtectedWalletMessage(intent, { openDialog: false });
      activeIntent = null;
      cancelIntent(intent);
      return;
    }
    if (mutationStatus === 'timeout') emit('mutation-timeout', { surface: intent.surface });

    const button = await waitForCheckoutButton(intent, {
      signal: intent.controller.signal,
      isCurrent: () => intentIsCurrent(intent),
    });
    if (!intentIsCurrent(intent)) return;

    if (!button) {
      activeIntent = null;
      cancelIntent(intent);
      navigateToCartFallback('checkout-button-timeout', intent.surface);
      return;
    }
    if (!formIsValid(button)) {
      activeIntent = null;
      cancelIntent(intent);
      return;
    }
    if (walletMutationIsBusy()) {
      showProtectedWalletMessage(intent, { openDialog: false });
      activeIntent = null;
      cancelIntent(intent);
      return;
    }
    if (handleWholesaleFirstClick({ preventDefault() {} }, button)) {
      activeIntent = null;
      cancelIntent(intent);
      return;
    }

    activeIntent = null;
    cancelIntent(intent);
    emit('continue', {
      source: 'signed-in-deferred',
      choice: 'login',
      surface: intent.surface,
      mode: root.dataset.mode,
    });
    submitNativeCheckout(button, intent.surface);
  }

  function beginSignedInCheckout(button) {
    const intent = replaceActiveIntent(button);
    void prepareSignedInCheckout(intent);
  }

  async function startLogin() {
    const intent = activeIntent;
    if (!intentIsCurrent(intent) || loginOperation) return;

    let checkoutButton = resolveCheckoutButton(intent);
    if (!checkoutButton || !formIsValid(checkoutButton)) return;

    const loginUrl = root.dataset.loginUrl;
    if (!loginUrl) {
      showError('Không thể mở đăng nhập lúc này. Bạn vẫn có thể mua không đăng nhập.');
      return;
    }

    setBusy(true);
    showError('');

    const controller = createAbortHandle();
    const operation = {
      intent,
      controller,
      timeoutId: null,
      timedOut: false,
      abortReason: '',
    };
    loginOperation = operation;

    const onIntentAbort = () => {
      operation.abortReason = operation.abortReason || 'intent-cancelled';
      try {
        controller.abort();
      } catch (error) {
        // The login fetch is already ending.
      }
    };
    if (intent.controller.signal && typeof intent.controller.signal.addEventListener === 'function') {
      intent.controller.signal.addEventListener('abort', onIntentAbort, { once: true });
    }

    operation.timeoutId = window.setTimeout(() => {
      operation.timedOut = true;
      try {
        controller.abort();
      } catch (error) {
        // The login operation has already completed.
      }
    }, LOGIN_TIMEOUT_MS);

    let navigating = false;

    try {
      const mutationStatus = await waitForCartIdle({
        timeout: LOGIN_TIMEOUT_MS,
        signal: controller.signal,
        isCurrent: () => intentIsCurrent(intent),
      });
      if (mutationStatus === 'protected-timeout') throw new Error('wallet-protected-timeout');
      if (mutationStatus !== 'idle') throw new Error(mutationStatus === 'timeout' ? 'mutation-timeout' : 'login-aborted');

      checkoutButton = resolveCheckoutButton(intent);
      if (!checkoutButton || !formIsValid(checkoutButton)) throw new Error('checkout-button-missing');
      if (walletMutationIsBusy()) throw new Error('wallet-protected-timeout');

      await persistCartFields(checkoutButton, controller.signal);
      if (!intentIsCurrent(intent) || controller.signal?.aborted) throw new Error('login-aborted');

      checkoutButton = resolveCheckoutButton(intent);
      if (!checkoutButton || !formIsValid(checkoutButton)) throw new Error('checkout-button-missing');
      if (walletMutationIsBusy()) throw new Error('wallet-protected-timeout');

      safeStorageSet(
        resumeCheckoutKey,
        JSON.stringify({
          createdAt: Date.now(),
          path: window.location.pathname,
          surface: intent.surface,
        })
      );
      clearGuestTestMode();
      emit('choice', { choice: 'login', surface: intent.surface, mode: root.dataset.mode });

      const target = sanitizedLoginUrl(loginUrl);
      window.location.assign(target);
      navigating = true;
    } catch (error) {
      const intentionallyCancelled =
        !intentIsCurrent(intent) ||
        intent.controller.signal?.aborted ||
        (operation.abortReason && operation.abortReason !== 'timeout');
      if (intentionallyCancelled) return;

      console.warn('[QVA account gateway] Login preparation did not complete.');
      const protectedWalletTimeout =
        error?.message === 'wallet-protected-timeout' || (operation.timedOut && walletMutationIsBusy());
      showError(
        protectedWalletTimeout
          ? WALLET_BUSY_MESSAGE
          : operation.timedOut || error?.message === 'mutation-timeout'
            ? 'Việc chuẩn bị đăng nhập mất quá lâu. Bạn có thể thử lại hoặc tiếp tục mua không đăng nhập.'
            : 'Chưa thể lưu thông tin giỏ hàng. Hãy thử lại hoặc tiếp tục mua không đăng nhập.'
      );
      if (protectedWalletTimeout) {
        emit('wallet-protected-timeout', { surface: intent.surface, mode: root.dataset.mode });
      }
      setBusy(false);
    } finally {
      window.clearTimeout(operation.timeoutId);
      if (intent.controller.signal && typeof intent.controller.signal.removeEventListener === 'function') {
        intent.controller.signal.removeEventListener('abort', onIntentAbort);
      }
      if (loginOperation === operation) loginOperation = null;
      if (!navigating && intentIsCurrent(intent) && operation.timedOut) setBusy(false);
    }
  }

  function interceptCheckout(event, button, phase) {
    if (!button) return;

    const nativeContinuation = nativeContinuationFor(button);
    if (nativeContinuation) {
      /* Cart Select's own mutation token is expected to remain active until
         requestSubmit returns, but protected Wallet work is never bypassable. */
      if (walletMutationIsBusy()) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
        revokeNativeContinuation(nativeContinuation);
        showProtectedWalletNotice();
        emit('wallet-protected-timeout', { surface: nativeContinuation.surface, mode: root.dataset.mode });
        return;
      }
      if (phase === 'submit') {
        consumeNativeContinuation(nativeContinuation);
        // A later submit listener may still prevent navigation. Release only
        // presentation here; the continuation authority remains consumed.
        Promise.resolve().then(() => {
          if (event.defaultPrevented) clearCheckoutProgress(nativeContinuation);
        });
      }
      return;
    }
    if (blockDuplicateCheckout(event, button)) return;
    if (customerAuthenticated) {
      const drawerSubmitterWasLost =
        phase === 'submit' && checkoutSurface(button) === 'drawer' && !isCheckoutButton(event.submitter);
      if (!cartMutationIsBusy() && !drawerSubmitterWasLost) return;
      if (!formIsValid(button)) return;
      event.preventDefault();
      beginSignedInCheckout(button);
      return;
    }
    if (!formIsValid(button)) return;

    // Deliberately do not return for event.defaultPrevented. The drawer has a
    // known path where another handler prevents the event without continuing it.
    event.preventDefault();
    beginCheckoutIntent(button);
  }

  function handleWholesaleFirstClick(event, button) {
    const drawer = button?.closest('cart-drawer');
    const cards = Array.from(document.querySelectorAll('qva-gia-si-optin'));
    const card = drawer
      ? cards.find((candidate) => candidate.closest('cart-drawer') === drawer)
      : cards.find((candidate) => !candidate.closest('cart-drawer') && candidate.offsetParent !== null);
    const checkbox = card?.querySelector('.qva-optin__box');
    if (!card || !checkbox || checkbox.checked || card.dataset.qvaWarned === '1') return false;

    event.preventDefault();
    card.dataset.qvaWarned = '1';
    card.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });

    const message = card.querySelector('.qva-optin__t2');
    if (message) {
      message.hidden = false;
      message.innerHTML = card.dataset.potential
        ? `Bạn đang bỏ lỡ <strong>${card.dataset.potential}</strong> — bấm ô này để nhận`
        : `Bạn đang bỏ lỡ ưu đãi <strong>giảm ${card.dataset.rate || ''}%</strong> — bấm ô này để nhận`;
    }

    return true;
  }

  function buyNowRequestId() {
    buyNowSequence += 1;
    return `qva-buy-now-${Date.now().toString(36)}-${buyNowSequence.toString(36)}`;
  }

  function clearBuyNowWalletRetry(record) {
    if (!record) return;
    window.clearTimeout(record.walletRetryTimeoutId);
    record.walletRetryTimeoutId = null;
    record.walletRetryState = 'done';
  }

  function releaseBuyNow(record) {
    if (!record) return;
    window.clearTimeout(record.timeoutId);
    clearBuyNowWalletRetry(record);
    if (pendingBuyNow === record) pendingBuyNow = null;
    record.state = 'released';

    const { button, form, requestId } = record.intent;
    if (button?.dataset.qvaBuyNowRequestId === requestId) {
      button.removeAttribute('data-qva-buy-now-request-id');
    }
    if (form?.dataset.qvaBuyNowRequestId === requestId) {
      form.removeAttribute('data-qva-buy-now-request-id');
    }
    button?.removeAttribute('aria-busy');
    button?.removeAttribute('data-qva-buy-now-busy');
  }

  function stopTimedOutBuyNowAfterWallet(record, reason) {
    if (pendingBuyNow !== record || record.state !== 'timed-out') return;
    emit('buy-now-wallet-retry-stopped', { mode: root.dataset.mode, reason });
    showProtectedWalletNotice();
    releaseBuyNow(record);
  }

  function queueTimedOutBuyNowFallbackAfterWallet(record) {
    if (
      pendingBuyNow !== record ||
      record.state !== 'timed-out' ||
      record.walletRetryState !== 'none'
    ) {
      return false;
    }

    record.walletRetryState = 'queued';
    emit('buy-now-wallet-retry-queued', { mode: root.dataset.mode });
    record.walletRetryTimeoutId = window.setTimeout(() => {
      if (pendingBuyNow !== record || record.state !== 'timed-out') return;

      record.walletRetryTimeoutId = null;
      record.walletRetryState = 'expired';
      if (walletMutationIsBusy()) {
        stopTimedOutBuyNowAfterWallet(record, 'protected-wallet-end-timeout');
        return;
      }

      releaseBuyNow(record);
      navigateToCartFallback('buy-now-wallet-end-timeout');
    }, BUY_NOW_WALLET_RETRY_TIMEOUT_MS);
    return true;
  }

  function retryTimedOutBuyNowFallbackAfterWallet() {
    const record = pendingBuyNow;
    if (!record || record.state !== 'timed-out' || record.walletRetryState !== 'queued') return;

    // Claim this single retry before yielding. A second protected end event
    // cannot schedule another continuation for the same accepted add.
    record.walletRetryState = 'running';
    window.clearTimeout(record.walletRetryTimeoutId);
    record.walletRetryTimeoutId = null;

    window.setTimeout(async () => {
      const isCurrent = () =>
        pendingBuyNow === record && record.state === 'timed-out' && record.walletRetryState === 'running';
      if (!isCurrent()) return;

      const mutationStatus = await waitForCartIdle({
        timeout: CART_IDLE_TIMEOUT_MS,
        isCurrent,
      });
      if (!isCurrent() || mutationStatus === 'aborted') return;

      if (mutationStatus === 'protected-timeout' || walletMutationIsBusy()) {
        stopTimedOutBuyNowAfterWallet(record, 'protected-wallet-still-busy');
        return;
      }

      // The product add was already accepted. Only release its correlation and
      // go to the cart; never submit or click the product form again here.
      releaseBuyNow(record);
      navigateToCartFallback('buy-now-wallet-end-retry');
    }, 0);
  }

  function findCheckoutButtonAfterAdd() {
    return (
      firstUsableCheckoutButton('cart-drawer [name="checkout"]') ||
      firstUsableCheckoutButton('cart-notification [name="checkout"]') ||
      firstUsableCheckoutButton('main [name="checkout"]')
    );
  }

  function wholesaleNeedsAttention(button) {
    const drawer = button?.closest('cart-drawer');
    const card = drawer?.querySelector('qva-gia-si-optin');
    const checkbox = card?.querySelector('.qva-optin__box');
    return Boolean(card && checkbox && !checkbox.checked && card.dataset.qvaWarned !== '1');
  }

  function continueBuyNowAfterAdd(record, attempt = 0) {
    if (!record || record.state !== 'ready') return;

    const checkoutButton = findCheckoutButtonAfterAdd();
    if (!checkoutButton) {
      if (attempt < 30) {
        window.setTimeout(() => continueBuyNowAfterAdd(record, attempt + 1), 50);
      } else {
        releaseBuyNow(record);
        navigateToCartFallback('buy-now-checkout-missing');
      }
      return;
    }

    const drawer = checkoutButton.closest('cart-drawer');
    if (drawer && wholesaleNeedsAttention(checkoutButton) && typeof drawer.open === 'function') {
      drawer.open(record.intent.button);
      window.setTimeout(() => {
        if (record.state !== 'ready') return;
        releaseBuyNow(record);
        const liveButton = findCheckoutButtonAfterAdd();
        if (liveButton) {
          liveButton.click();
        } else {
          navigateToCartFallback('buy-now-checkout-missing');
        }
      }, 260);
      return;
    }

    const surface = checkoutSurface(checkoutButton);
    releaseBuyNow(record);
    const liveButton = resolveCheckoutButton(surface) || findCheckoutButtonAfterAdd();
    if (liveButton) {
      liveButton.click();
    } else {
      navigateToCartFallback('buy-now-checkout-missing');
    }
  }

  function rememberBuyNowIntent(event, button) {
    if (!isBuyNowButton(button)) return;

    if (button.dataset.qvaBuyNowBusy === 'true' || pendingBuyNow) {
      event.preventDefault();
      return;
    }

    const form = button.form;
    const variantId = form?.querySelector('[name="id"]')?.value;
    if (!form || !variantId || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const requestId = buyNowRequestId();
    const intent = Object.freeze({
      button,
      form,
      requestId,
      variantId: String(variantId),
      createdAt: Date.now(),
    });
    const record = {
      intent,
      timeoutId: null,
      state: 'pending',
      walletRetryState: 'none',
      walletRetryTimeoutId: null,
    };

    record.timeoutId = window.setTimeout(() => {
      if (pendingBuyNow === record && record.state === 'pending') {
        // Keep the correlation alive so a late ready/error event is still
        // consumed exactly once. Navigating to the cart avoids a second add.
        record.state = 'timed-out';
        emit('buy-now-timeout', { mode: root.dataset.mode });
        const fallbackStarted = navigateToCartFallback('buy-now-event-timeout');
        if (!fallbackStarted && walletMutationIsBusy()) {
          queueTimedOutBuyNowFallbackAfterWallet(record);
        }
      }
    }, BUY_NOW_TIMEOUT_MS);

    pendingBuyNow = record;
    button.dataset.qvaBuyNowBusy = 'true';
    button.dataset.qvaBuyNowRequestId = requestId;
    form.dataset.qvaBuyNowRequestId = requestId;
    button.setAttribute('aria-busy', 'true');
  }

  function buyNowEventMatches(record, event) {
    if (!record || (record.state !== 'pending' && record.state !== 'timed-out')) return false;
    const detail = event?.detail || {};
    if (detail.source && detail.source !== 'product-form') return false;
    if (String(detail.requestId || '') !== record.intent.requestId) return false;

    const eventVariantId = detail.productVariantId ?? detail.variantId;
    return eventVariantId == null || String(eventVariantId) === record.intent.variantId;
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const button = target instanceof Element ? target.closest('[data-qva-buy-now]') : null;
      rememberBuyNowIntent(event, button);
    },
    true
  );

  document.addEventListener('qva:buy-now-cart-ready', (event) => {
    const record = pendingBuyNow;
    if (!buyNowEventMatches(record, event)) return;

    record.state = 'ready';
    window.clearTimeout(record.timeoutId);
    continueBuyNowAfterAdd(record);
  });

  document.addEventListener('qva:buy-now-cart-reconcile', (event) => {
    const record = pendingBuyNow;
    if (!buyNowEventMatches(record, event)) return;

    // Product Form owns the one Wallet-safe navigation to server cart truth.
    // Releasing here only cancels this gateway's timeout/checkout continuation.
    releaseBuyNow(record);
  });

  document.addEventListener('qva:buy-now-error', (event) => {
    const record = pendingBuyNow;
    if (!buyNowEventMatches(record, event)) return;
    releaseBuyNow(record);
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const button = target instanceof Element ? target.closest('[name="checkout"]') : null;
      if (!isCheckoutButton(button)) return;
      if (blockDuplicateCheckout(event, button)) return;

      if (!nativeContinuationIsActive(button) && handleWholesaleFirstClick(event, button)) return;
      interceptCheckout(event, button, 'click');
    },
    true
  );

  document.addEventListener('submit', (event) => {
    const button = checkoutButtonFromSubmitEvent(event);
    if (!button) return;
    if (blockDuplicateCheckout(event, button)) return;
    if (!nativeContinuationIsActive(button) && handleWholesaleFirstClick(event, button)) return;
    interceptCheckout(event, button, 'submit');
  });

  loginButton?.addEventListener('click', startLogin);
  guestButton?.addEventListener('click', () => {
    const intent = activeIntent;
    if (intent) void continueOriginalCheckout(intent, 'guest');
  });
  closeButtons.forEach((button) => button.addEventListener('click', () => closeGateway('dismiss')));

  window.addEventListener(
    'keydown',
    (event) => {
      if (!gatewayIsOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeGateway('escape');
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!dialog.contains(document.activeElement) || document.activeElement === dialog) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    true
  );

  async function resumeSignedInCheckout() {
    const resume = safeStorageJson(resumeCheckoutKey);
    const createdAt = Number(resume?.createdAt);
    const fresh =
      Number.isFinite(createdAt) && Date.now() - createdAt >= 0 && Date.now() - createdAt < RESUME_MAX_AGE_MS;
    const returnedToOrigin = fresh && resume.path === window.location.pathname;

    if (!returnedToOrigin) {
      safeStorageRemove(resumeCheckoutKey);
      return;
    }

    const surface = ['drawer', 'notification', 'cart-page'].includes(resume.surface) ? resume.surface : 'cart-page';
    const mutationStatus = await waitForCartIdle({ timeout: CART_IDLE_TIMEOUT_MS });
    if (mutationStatus === 'protected-timeout' || walletMutationIsBusy()) {
      safeStorageRemove(resumeCheckoutKey);
      emit('wallet-protected-timeout', { surface, mode: root.dataset.mode });
      showProtectedWalletNotice();
      return;
    }
    const button = await waitForCheckoutButton(surface, { timeout: 4000 });

    safeStorageRemove(resumeCheckoutKey);
    if (walletMutationIsBusy()) {
      emit('wallet-protected-timeout', { surface, mode: root.dataset.mode });
      showProtectedWalletNotice();
      return;
    }
    if (!button) {
      navigateToCartFallback('resume-checkout-missing', surface);
      return;
    }
    if (!formIsValid(button)) return;

    emit('continue', {
      source: 'signed-in-resume',
      choice: 'login',
      surface,
      mode: root.dataset.mode,
    });
    submitNativeCheckout(button, surface);
  }

  if (customerAuthenticated) {
    void resumeSignedInCheckout();
  } else {
    // A cancelled/failed login must not trigger an unrelated future sign-in.
    safeStorageRemove(resumeCheckoutKey);
  }
})();
